import { useState, useEffect } from 'react';
import './Projects.css';

const PROJECT_CATEGORIES = ['All', 'Human Development', 'Peace & Rights', 'SDG Action', 'Infrastructural Funding'];

function Projects({ siteData }) {
  const projects = siteData?.projects || [];
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  // Close modal on Escape key press & lock scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedProject(null);
    };
    if (selectedProject) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedProject]);

  // Expanded projects list combining siteData with Infrastructural Funding projects
  const extendedProjects = [
    ...projects,
    {
      id: 'p4',
      title: 'Residential Housing & Urban Infrastructure',
      tag: 'Infrastructural Funding',
      image: 'https://images.unsplash.com/photo-1541888946425-d0fbb186a5b3?auto=format&fit=crop&w=1200&q=80',
      shortDescription: 'Arranging funding facilities for residential housing, civil structures, and commercial sectors.',
      description: 'We arrange funding facilities from international donors to governments for residential housing projects, hospitals, schools, and civic infrastructure with full government buy-back options.'
    },
    {
      id: 'p5',
      title: 'Renewable Green Energy & Clean Tech',
      tag: 'Infrastructural Funding',
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1200&q=80',
      shortDescription: 'Supporting solar, wind, and sustainable water & sanitation infrastructure.',
      description: 'Empowering developing nations with clean energy grids, solar farming, and modern water treatment facilities to accelerate climate action.'
    }
  ];

  const filteredProjects = extendedProjects.filter((p) => {
    if (activeFilter === 'All') return true;
    return p.tag === activeFilter;
  });

  return (
    <div className="page-shell projects-page">
      <div className="page-intro projects-hero">
        <span className="projects-badge">Global Initiatives & Impact</span>
        <h1 className="page-title">Featured Projects & Programs</h1>
        <p className="page-subtitle">
          Our initiatives promote sustainable socio-economic development, peace advocacy, human rights education, and international infrastructural funding.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="project-filter-bar">
        {PROJECT_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`project-filter-btn ${activeFilter === cat ? 'active' : ''}`}
            onClick={() => setActiveFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Project Cards Grid */}
      <div className="project-grid">
        {filteredProjects.map((project) => (
          <article
            className="project-card"
            key={project.id}
            onClick={() => setSelectedProject(project)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedProject(project);
              }
            }}
          >
            <div className="project-img-wrap">
              <img src={project.image} alt={project.title} />
              <span className="project-tag">{project.tag}</span>
            </div>
            <div className="project-card-content">
              <h3>{project.title}</h3>
              <p className="project-short">{project.shortDescription}</p>
              <p className="project-full">{project.description}</p>
              <button
                type="button"
                className="project-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProject(project);
                }}
              >
                Inquire & Learn More →
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedProject && (
        <div className="project-modal-backdrop" onClick={() => setSelectedProject(null)}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <div>
                <span className="project-modal-tag">{selectedProject.tag}</span>
                <h2>{selectedProject.title}</h2>
              </div>
              <button className="project-modal-close" onClick={() => setSelectedProject(null)} aria-label="Close modal">✕</button>
            </div>
            <div className="project-modal-body">
              <img src={selectedProject.image} alt={selectedProject.title} className="project-modal-img" />
              <h4>Project Scope & Implementation</h4>
              <p>{selectedProject.description}</p>
              <div className="project-action-box">
                <h5>Partner or Request Funding with UNGDO:</h5>
                <p>Contact our Head Office in Delaware, USA to discuss partnership opportunities, governmental funding arrangements, or volunteer involvement.</p>
                <a href="mailto:info@ungdo.org?subject=Project Inquiry" className="primary-button" style={{ display: 'inline-block', textDecoration: 'none' }}>
                  Send Email Inquiry ✉️
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
